// One public section (Rooms, Gallery, Contact…) on its own URL with hero band + SEO.
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import Rooms from '../components/Rooms';
import Amenities from '../components/Amenities';
import Gallery from '../components/Gallery';
import VirtualTour from '../components/VirtualTour';
import Location from '../components/Location';
import AboutDestination from '../components/AboutDestination';
import Reviews from '../components/Reviews';
import FAQ from '../components/FAQ';
import Contact from '../components/Contact';
import ContactMapHours from '../components/ContactMapHours';
import KeyFacts from '../components/KeyFacts';
import SectionIntro from '../components/SectionIntro';
import { useLanguage } from '../context/LanguageContext';
import { SECTIONS, homePath } from '../siteRoutes';
import { getIntro } from '../i18n/intros';
import UlcinjGuide from '../components/UlcinjGuide';

const BODY = {
  rooms: () => <Rooms />,
  amenities: () => <Amenities />,
  gallery: () => <Gallery />,
  'virtual-tour': () => <VirtualTour />,
  location: () => <><Location /><AboutDestination /></>,
  reviews: () => <Reviews withSchema />,
  faq: () => <FAQ />,
  contact: () => <><ContactMapHours /><Contact /></>,
  'ulcinj-guide': () => <UlcinjGuide />,
};

export default function SectionPage({ slug }) {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const section = SECTIONS.find((s) => s.slug === slug);
  const title = t(section.titleKey);
  const desc = t(section.descKey);
  const intro = getIntro(lang, slug);
  const Body = BODY[slug];

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [slug]);

  const seoOverride = section.seo ? section.seo(lang) : null;
  const SITE = (process.env.REACT_APP_SITE_URL || 'https://larosa-negra.com').replace(/\/$/, '');
  const langPrefix = lang === 'en' ? '' : `/${lang}`;
  const breadcrumbSchema = { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Hotel La Rosa Negra', item: `${SITE}${langPrefix}/` },
    { '@type': 'ListItem', position: 2, name: t(section.navKey), item: `${SITE}${langPrefix}/${slug}` },
  ] };
  const metaDesc = seoOverride ? seoOverride.description : intro ? intro.replace(/\s+/g, ' ').slice(0, 155).replace(/[,;:\s]+\S*$/, '') + '…' : (desc !== title ? `${title} — ${desc}. Hotel La Rosa Negra, Velika Plaža, Ulcinj, Montenegro.` : undefined);

  return (
    <div data-testid={`section-page-${slug}`}>
      <SEOHead url={`/${slug}`} faq={slug === 'faq'} type={seoOverride ? 'article' : 'website'} title={seoOverride ? seoOverride.title : `${title} · Hotel La Rosa Negra, Ulcinj`} description={metaDesc} schema={seoOverride ? null : breadcrumbSchema} />
      <Header />
      <section className="relative bg-[#0f1318] text-white pt-36 pb-12 sm:pt-40 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(231,117,0,0.25),transparent_60%)]" />
        <div className="container mx-auto px-4 relative">
          <nav className="flex items-center gap-1 text-xs uppercase tracking-widest text-orange-400 mb-3" aria-label="Breadcrumb" data-testid="section-breadcrumb">
            <Link to={homePath(location.pathname)} className="hover:text-orange-300">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-300">{t(section.navKey)}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold" data-testid="section-title">{title}</h1>
          {desc && desc !== title && <p className="mt-3 text-base md:text-lg text-gray-300 max-w-2xl">{desc}</p>}
        </div>
      </section>
      <SectionIntro slug={slug} />
      {(slug === 'rooms' || slug === 'location') && <KeyFacts compact />}
      <Body />
      <Footer />
    </div>
  );
}
